/**
 * ================================================================
 * AUTH SERVICE - Unified
 * ================================================================
 *
 * Single source of truth for Authentication.
 * Consolidates all legacy .api.ts and .client.ts auth services.
 *
 * Features:
 * - Better Auth Integration (Sign In, Sign Up, Session)
 * - OTP Management (Email & Phone)
 * - Registration Flow (DTOs, Session Storage)
 * - Password Management (Reset, Update)
 * - Input Validation (Email, Phone duplication check)
 *
 * @module features/auth/services
 */

import { createAuthClient } from "better-auth/react";
import { emailOTPClient, phoneNumberClient } from "better-auth/client/plugins";
import { z } from "zod";
import {
  usersControllerGetProfile,
  usersControllerCheckEmail,
  usersControllerCheckPhone,
  usersControllerFindByPhone,
  otpControllerSendOtp,
  otpControllerResetPassword,
  customInstance,
} from "@mebi/api-client";
import { normalizePhoneNumber } from "@/lib/utils";
import { translateError } from "@/lib/errorTranslator";
import { sessionService } from "./session.service";
import { registrationStorage } from "../utils/registrationStorage";
import { loginSchema, signupSchema } from "@mebi/schemas";

// ============================================
// HELPER FUNCTIONS
// ============================================

const SESSION_CHECK_TIMEOUT_MS = 4000;

const extractTrimmedText = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const extractErrorTextFromArray = (
  values: unknown[],
  depth: number,
): string | undefined => {
  for (const item of values) {
    const nested = extractNestedErrorText(item, depth);
    if (nested) return nested;
  }
  return undefined;
};

const extractErrorTextFromObject = (
  record: Record<string, unknown>,
  depth: number,
): string | undefined => {
  const priorityKeys = [
    "message",
    "error",
    "statusText",
    "detail",
    "reason",
    "cause",
  ];

  for (const key of priorityKeys) {
    const nested = extractNestedErrorText(record[key], depth);
    if (nested) return nested;
  }

  const responseRecord =
    typeof record.response === "object" && record.response
      ? (record.response as Record<string, unknown>)
      : undefined;

  if (!responseRecord) {
    return undefined;
  }

  return (
    extractNestedErrorText(responseRecord.data, depth) ||
    extractNestedErrorText(responseRecord.error, depth)
  );
};

const extractNestedErrorText = (
  value: unknown,
  depth = 0,
): string | undefined => {
  if (depth > 4 || value === null || value === undefined) {
    return undefined;
  }

  const directText = extractTrimmedText(value);
  if (directText) {
    return directText;
  }

  if (value instanceof Error) {
    return (
      extractNestedErrorText(value.message, depth + 1) ||
      extractNestedErrorText((value as { cause?: unknown }).cause, depth + 1)
    );
  }

  if (Array.isArray(value)) {
    return extractErrorTextFromArray(value, depth + 1);
  }

  if (typeof value === "object") {
    return extractErrorTextFromObject(
      value as Record<string, unknown>,
      depth + 1,
    );
  }

  return undefined;
};

const normalizePasswordUpdateError = (value: unknown): string => {
  const extracted = extractNestedErrorText(value);
  const translated = extracted ? translateError(extracted) : "";

  if (translated && translated !== "Đã có lỗi xảy ra") {
    return translated;
  }

  if (extracted) {
    const normalized = extracted.toLowerCase();

    const isGenericErrorMessage = [
      "đã có lỗi xảy ra",
      "có lỗi đã xảy ra",
      "something went wrong",
      "internal server error",
      "unknown error",
    ].some((message) => normalized.includes(message));

    if (isGenericErrorMessage) {
      return "Đổi mật khẩu không thành công. Vui lòng kiểm tra mật khẩu hiện tại và thử lại.";
    }

    if (
      normalized.includes("current password") ||
      normalized.includes("invalid current password") ||
      normalized.includes("wrong password")
    ) {
      return "Mật khẩu hiện tại không đúng";
    }

    return extracted;
  }

  return "Có lỗi xảy ra khi đổi mật khẩu";
};

type PasswordChangeFallbackResponse = {
  data?: {
    success?: boolean;
    message?: string;
  };
};

type DeleteAccountResponse = {
  data?: {
    success?: boolean;
    message?: string;
  };
};

const updatePasswordWithBearerFallback = async (
  currentPassword: string,
  newPassword: string,
): Promise<AuthResult<unknown>> => {
  try {
    const response = await customInstance<PasswordChangeFallbackResponse>(
      "/otp/change-password",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      },
    );

    const payload = response?.data;

    if (payload?.success === false) {
      return {
        success: false,
        error: normalizePasswordUpdateError(payload.message),
      };
    }

    return {
      success: true,
      message: payload?.message || "Đổi mật khẩu thành công",
      data: payload,
    };
  } catch (error) {
    return {
      success: false,
      error: normalizePasswordUpdateError(error),
    };
  }
};

const resolveWithTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
): Promise<T | null> => {
  return new Promise<T | null>((resolve, reject) => {
    const timeoutId = setTimeout(() => resolve(null), timeoutMs);

    promise
      .then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
};

// ============================================
// BETTER AUTH INIT
// ============================================

const resolveAuthBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL?.trim();
  const baseUrl =
    envUrl && envUrl.length > 0 ? envUrl : globalThis.location?.origin || "";
  return baseUrl.replace(/\/$/, "");
};

const API_URL = resolveAuthBaseUrl();

export const betterAuthClient = createAuthClient({
  baseURL: API_URL,
  basePath: "/api/auth",
  plugins: [emailOTPClient(), phoneNumberClient()],
});

// ============================================
// TYPES
// ============================================

export interface User {
  id: string;
  email?: string;
  phone?: string;
  fullName?: string;
  avatarUrl?: string;
}

export interface Session {
  access_token: string;
  expires_at?: number;
  user: User;
}

export interface AuthResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface OTPResponse {
  otpSent?: boolean;
  expiresAt?: string;
  requiresOTP?: boolean;
  otpId?: string;
}

export interface OTPVerifyResponse {
  verified: boolean;
  otpId?: string;
}

export interface OTPTokenResponse {
  otpId: string;
}

export interface RegisterRequest {
  phone: string;
  email: string;
  password?: string;
  fullName?: string;
}

export type AuthPurpose = "register" | "forgot-password" | "sign-in";
type OtpRequestMethod = "email" | "sms" | "zns";

export interface OTPRequestParams {
  phone: string;
  email?: string;
  purpose: AuthPurpose;
  method?: OtpRequestMethod;
}

export interface OTPVerifyParams {
  phone: string;
  email?: string;
  otp: string;
  purpose: AuthPurpose;
  password?: string; // For registration flow
  method?: OtpRequestMethod;
}

const signInSchema = loginSchema;
const signUpSchema = signupSchema;

type SignInRequest = z.infer<typeof signInSchema>;

// Schema for Better Auth Response
const UserSchema = z.object({
  id: z.string(),
  email: z.string().optional(),
  phone: z.string().nullable().optional(), // Allow null
  phoneNumber: z.string().nullable().optional(), // Better Auth phone plugin field
  name: z.string(),
  image: z.string().nullable().optional(), // Allow null
});

const SessionSchema = z.object({
  token: z.string(),
  expiresAt: z.union([z.date(), z.string()]),
});

const BetterAuthResponseSchema = z.object({
  user: UserSchema,
  session: SessionSchema.optional(),
  token: z.string().optional(),
});

const AuthContractUserSchema = z
  .object({
    id: z.union([z.string(), z.number()]).transform(String),
    email: z.string().nullable().optional().or(z.literal("")),
    phone: z.string().nullable().optional().or(z.literal("")),
    phoneNumber: z.string().nullable().optional().or(z.literal("")),
    fullName: z.string().nullable().optional().or(z.literal("")),
    name: z.string().nullable().optional().or(z.literal("")),
    avatarUrl: z.string().nullable().optional().or(z.literal("")),
    image: z.string().nullable().optional().or(z.literal("")),
  })
  .transform((raw) => ({
    id: raw.id,
    email: raw.email || undefined,
    phone: raw.phone || raw.phoneNumber || undefined,
    fullName: raw.fullName || raw.name || undefined,
    avatarUrl: raw.avatarUrl || raw.image || undefined,
  }));

const extractAuthContractUser = (payload: unknown): User | undefined => {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }

  const rawUser = (payload as { user?: unknown }).user;
  const parsed = AuthContractUserSchema.safeParse(rawUser);

  if (!parsed.success) {
    return undefined;
  }

  return parsed.data;
};

// ============================================
// HELPER FUNCTIONS
// ============================================

async function handleAuthResponse(
  data: unknown,
): Promise<AuthResult<{ user: User; session: Session }>> {
  // Parse response with Zod
  const parsedData = BetterAuthResponseSchema.safeParse(data);

  if (!parsedData.success) {
    console.error("[AuthService] Invalid response format:", parsedData.error);
    return {
      success: false,
      error: "Dữ liệu phản hồi không hợp lệ",
    };
  }

  const rawData = parsedData.data;

  // Better Auth returns token in data.token or data.session.token
  let accessToken = "";
  let expiresAtDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // Default 7 days

  if (rawData.session) {
    accessToken = rawData.session.token;
    expiresAtDate = new Date(rawData.session.expiresAt);
  } else if (rawData.token) {
    accessToken = rawData.token;
    // Expiry not provided in flat structure, use default
  } else {
    console.error(
      "[AuthService] No session or token returned in data:",
      rawData,
    );
    return {
      success: false,
      error: "Lỗi xác thực: Không tìm thấy phiên làm việc",
    };
  }

  const user: User = {
    id: rawData.user.id,
    email: rawData.user.email,
    phone: rawData.user.phone || rawData.user.phoneNumber || undefined,
    fullName: rawData.user.name,
    avatarUrl: rawData.user.image || undefined, // Convert null to undefined
  };

  const session: Session = {
    access_token: accessToken,
    expires_at: Math.floor(expiresAtDate.getTime() / 1000),
    user,
  };

  // Persist tokens via Session Service
  await sessionService.storeTokens(session.access_token);

  return { success: true, data: { user, session } };
}

/**
 * AUTH CONTRACT RULE #1 + #2
 * - Backend-verified bearer token is the mobile source of truth for authentication state.
 * - Session data is treated as a secondary fallback for profile hydration only.
 */
async function resolveAuthenticatedUser(): Promise<User | null> {
  const storedToken = await sessionService.getAccessToken();

  if (storedToken) {
    try {
      const response = await usersControllerGetProfile();
      const payload = response as unknown as {
        data?: { user?: unknown };
        user?: unknown;
      };
      const userFromProfile = payload.data?.user || payload.user;
      const parsedFromProfile =
        AuthContractUserSchema.safeParse(userFromProfile);

      if (parsedFromProfile.success) {
        return parsedFromProfile.data;
      }
    } catch (error) {
      console.warn(
        "[AuthService] Could not resolve user from bearer token:",
        error,
      );
    }
  }

  try {
    const { data } = await betterAuthClient.getSession();
    const parsedFromSession = AuthContractUserSchema.safeParse(data?.user);
    if (parsedFromSession.success) {
      return parsedFromSession.data;
    }
  } catch (error) {
    console.warn("[AuthService] Could not resolve user from session:", error);
  }

  return null;
}

function getValidationErrorMessage(
  purpose: string,
  exists: boolean,
): string | null {
  if (purpose === "register" && exists) {
    return "Số điện thoại này đã được đăng ký";
  }
  if ((purpose === "forgot-password" || purpose === "sign-in") && !exists) {
    return "Số điện thoại này chưa được đăng ký";
  }
  return null;
}

async function handleOtpSending(
  purpose: string,
  otpMethod: OtpRequestMethod,
  normalizedPhone: string,
  emailToUse: string,
): Promise<AuthResult<void>> {
  try {
    const isEmail = otpMethod === "email";
    const target = isEmail ? emailToUse : normalizedPhone;

    if (isEmail && !emailToUse) {
      return { success: false, error: "Vui lòng cung cấp email." };
    }

    console.log(
      `[AuthService] Sending Custom OTP via ${otpMethod} to: ${target} for: ${purpose}`,
    );

    await otpControllerSendOtp({
      phone: normalizedPhone,
      email: isEmail ? emailToUse : undefined,
      purpose,
      channel: otpMethod,
    });

    return { success: true };
  } catch (err) {
    console.error("[AuthService] OTP Send Error:", err);
    return {
      success: false,
      error: translateError(err) || "Có lỗi xảy ra khi gửi mã xác thực",
    };
  }
}

// ============================================
// MAIN SERVICE
// ============================================

export const authService = {
  /**
   * CORE AUTHENTICATION (Sign In, Session, Sign Out)
   */

  async signIn(
    credentials: SignInRequest,
  ): Promise<AuthResult<{ user: User; session: Session }>> {
    try {
      const { email, password } = signInSchema.parse(credentials);

      // Fallback cho tài khoản Email thực (cũ)
      const isRealEmail =
        email.includes("@") && !email.includes("@temp.mebi.vn");

      if (isRealEmail) {
        console.log(
          "[AuthService] Fallback to Email authentication for:",
          email,
        );
        const result = await resolveWithTimeout(
          betterAuthClient.signIn.email({
            email,
            password,
          }),
          SESSION_CHECK_TIMEOUT_MS * 2,
        );

        if (!result) {
          return {
            success: false,
            error: "Đăng nhập quá thời gian quy định (Timeout)",
          };
        }
        if (result.error) {
          return { success: false, error: translateError(result.error) };
        }
        return await handleAuthResponse(result.data);
      }

      // Luồng chuẩn: Xác thực bằng Số điện thoại (Normalize sẽ bóc đúng số từ "{sdt}@temp.mebi.vn" hoặc "{sdt}")
      const identifier = normalizePhoneNumber(email);
      console.log("[AuthService] Signing in with Phone:", identifier);

      const result = await resolveWithTimeout(
        betterAuthClient.signIn.phoneNumber({
          phoneNumber: identifier,
          password,
        }),
        SESSION_CHECK_TIMEOUT_MS * 2, // 8s timeout for login
      );

      if (!result) {
        return {
          success: false,
          error: "Đăng nhập quá thời gian quy định (Timeout)",
        };
      }

      console.log("[AuthService] raw result:", JSON.stringify(result, null, 2));

      if (result.error) {
        return {
          success: false,
          error: translateError(result.error.message || "Đăng nhập thất bại"),
        };
      }

      if (!result.data) {
        return {
          success: false,
          error: "Không nhận được phản hồi từ máy chủ",
        };
      }

      // Handle common response logic
      return await handleAuthResponse(result.data);
    } catch (err) {
      console.error("[AuthService] Sign in error:", err);
      return {
        success: false,
        error: translateError(err),
      };
    }
  },

  async signOut(): Promise<void> {
    try {
      await betterAuthClient.signOut();
      await sessionService.clearSession();
      // Auth store cleanup is handled by store actions
    } catch (err) {
      console.error("[AuthService] Sign out error:", err);
    }
  },

  async deleteAccount(nickname: string): Promise<AuthResult<unknown>> {
    const confirmedNickname = nickname.trim();

    if (!confirmedNickname) {
      return {
        success: false,
        error: "Vui lòng nhập biệt danh để xác nhận xoá tài khoản",
      };
    }

    try {
      const response = await customInstance<DeleteAccountResponse>(
        "/users/delete-account",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            nickname: confirmedNickname,
          }),
        },
      );

      const payload = response.data;

      if (payload?.success === false) {
        return {
          success: false,
          error: payload.message || "Không thể xoá tài khoản",
        };
      }

      return {
        success: true,
        message: payload?.message || "Xoá tài khoản thành công",
        data: payload,
      };
    } catch (error) {
      return {
        success: false,
        error: translateError(error) || "Không thể xoá tài khoản",
      };
    }
  },

  /**
   * REGISTRATION FLOW
   */

  // OTP Management
  otp: {
    // Step 1: Check phone availability & Send OTP
    async request(
      params: OTPRequestParams | string,
    ): Promise<AuthResult<OTPResponse>> {
      try {
        const phone = typeof params === "string" ? params : params.phone;
        const purpose =
          typeof params === "string" ? "register" : params.purpose;
        const normalizedPhone = normalizePhoneNumber(phone);

        // 1. Check if phone exists (Using UsersController)
        const response = await usersControllerCheckPhone({
          phone: normalizedPhone,
        });
        const exists = (response.data as unknown as { exists: boolean }).exists;

        // Handle error based on purpose
        const validationError = getValidationErrorMessage(purpose, exists);
        if (validationError) {
          return { success: false, error: validationError };
        }

        // 2. Save phone to temporary storage (only for register)
        if (purpose === "register") {
          registrationStorage.savePhone(normalizedPhone);
        }

        /**
         * 3. Send OTP
         * If the user selects email, call emailOtp.sendVerificationOtp.
         * Otherwise, call phoneNumber.sendOtp.
         */
        const otpMethod =
          typeof params === "string" ? "zns" : params.method || "zns";
        const emailToUse = typeof params === "string" ? "" : params.email || "";

        const sendResult = await handleOtpSending(
          purpose,
          otpMethod,
          normalizedPhone,
          emailToUse,
        );
        if (!sendResult.success) {
          return sendResult as AuthResult<OTPResponse>;
        }

        return {
          success: true,
          data: {
            otpSent: true,
            expiresAt: new Date(Date.now() + 5 * 60000).toISOString(), // 5 mins
            requiresOTP: true,
            otpId: "better-auth-managed", // Better Auth manages state internally via cookies/db
          },
        };
      } catch (err) {
        return {
          success: false,
          error: translateError(err),
        };
      }
    },

    // Step 2: Verify OTP
    async verify(
      params: OTPVerifyParams,
    ): Promise<AuthResult<OTPVerifyResponse>> {
      try {
        const otpMethod = params.method || "zns";
        console.log(
          `[AuthService] Verifying Custom ${otpMethod} OTP for: ${params.phone}`,
        );

        // Pass purpose from params if available, else infer based on flow. Actually mobile form might not pass purpose correctly but defaults to register/action
        const purpose = params.purpose || "register";

        await customInstance("/otp/verify", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phone: normalizePhoneNumber(params.phone),
            otp: params.otp,
            purpose,
            email: params.email,
            method: otpMethod,
          }),
        });

        // Simulate a minor delay for UX
        await new Promise((resolve) => setTimeout(resolve, 500));

        return {
          success: true,
          data: {
            verified: true,
            otpId: params.otp,
          },
        };
      } catch (err) {
        return {
          success: false,
          error: translateError(err) || "Mã OTP không chính xác",
        };
      }
    },

    // Legacy support
    async verifyToken(
      token?: string,
    ): Promise<AuthResult<{ verified: boolean }>> {
      const tokenValue = token?.trim() ?? "";
      const verified = token === undefined || tokenValue.length > 0;
      return { success: true, data: { verified } };
    },
  },

  // Step 3: Complete Registration
  async signUp(
    data: RegisterRequest,
  ): Promise<AuthResult<{ user: User; session?: Session }>> {
    try {
      const { email, password, fullName } = signUpSchema.parse(data);
      const normalizedPhone = normalizePhoneNumber(data.phone || "");

      // If we are doing phone-centric auth, we auto-generate email if missing
      const finalEmail = email || `${normalizedPhone}@mebi.vn`;

      const result = await betterAuthClient.signUp.email({
        email: finalEmail,
        password: password || "Test@123", // Required by Better Auth even if using phone/otp primarily
        name: fullName || "User",
        phoneNumber: normalizedPhone,
      });

      if (result.error) {
        return {
          success: false,
          error: translateError(result.error.message || "Đăng ký thất bại"),
        };
      }

      if (!result.data) {
        return {
          success: false,
          error: "Không nhận được phản hồi từ máy chủ",
        };
      }

      const authResponse = await handleAuthResponse(result.data);
      if (authResponse.success) {
        registrationStorage.clear();
        return authResponse;
      }

      // Sign-up can succeed without issuing a session/token immediately.
      const fallbackUser = extractAuthContractUser(result.data);
      if (fallbackUser) {
        registrationStorage.clear();
        return {
          success: true,
          data: {
            user: fallbackUser,
          },
        };
      }

      return authResponse;
    } catch (err) {
      return {
        success: false,
        error: translateError(err),
      };
    }
  },

  /**
   * USER PROFILE
   */
  async getProfile(): Promise<AuthResult<User>> {
    try {
      const userProfile = await resolveAuthenticatedUser();

      if (!userProfile) {
        return {
          success: false,
          error: "Không tìm thấy thông tin người dùng",
        };
      }

      return {
        success: true,
        data: userProfile,
      };
    } catch (err) {
      return {
        success: false,
        error: translateError(err),
      };
    }
  },

  /**
   * UTILS
   */
  async checkEmail(email: string): Promise<boolean> {
    try {
      const response = await usersControllerCheckEmail({ email });
      const exists = (response.data as unknown as { exists: boolean }).exists;
      return exists;
    } catch {
      return false;
    }
  },

  async checkPhone(phone: string): Promise<boolean> {
    try {
      const response = await usersControllerCheckPhone({ phone });
      const exists = (response.data as unknown as { exists: boolean }).exists;
      return exists;
    } catch (error) {
      // Log error for debugging but return false as expected for check function
      console.warn("[AuthService] Check phone error:", error);
      return false;
    }
  },

  password: {
    getUserByPhone: async (
      phone: string,
    ): Promise<AuthResult<{ id: string; email: string; phone: string }>> => {
      try {
        const normalized = normalizePhoneNumber(phone);
        const response = await usersControllerFindByPhone({
          phone: normalized,
        });

        // Depending on api client configuration, response might be the object directly or wrapped in data
        const responseData =
          response && "id" in response
            ? response
            : (response as { data?: unknown })?.data;

        if (!responseData || typeof responseData !== "object") {
          return { success: false, error: "Không tìm thấy tài khoản" };
        }

        const user = responseData as {
          id?: string;
          email?: string;
          phone?: string;
          phoneNumber?: string;
        };

        const matchedPhone = user.phoneNumber || user.phone;

        if (!user.id || !user.email || !matchedPhone) {
          return { success: false, error: "Không tìm thấy tài khoản" };
        }

        return {
          success: true,
          data: { id: user.id, email: user.email, phone: matchedPhone },
        };
      } catch (error: unknown) {
        console.error("[AuthService] Find user error:", error);
        return { success: false, error: "Không tìm thấy tài khoản" };
      }
    },
    forgotPasswordWithOTP: async (data: unknown) => {
      try {
        const payload = data as {
          phone?: string;
          otp?: string; // Reset token
          newPassword?: string;
        };

        if (!payload.phone || !payload.otp || !payload.newPassword) {
          return {
            success: false,
            error:
              "Vui lòng nhập đầy đủ thông tin số điện thoại, mã OTP và mật khẩu mới",
          };
        }

        await otpControllerResetPassword({
          phone: normalizePhoneNumber(payload.phone),
          otp: payload.otp,
          newPassword: payload.newPassword,
        });

        return {
          success: true,
          message: "Mật khẩu đã được thay đổi thành công",
        };
      } catch (error: unknown) {
        return {
          success: false,
          error:
            translateError(error) ||
            "Không thể đặt lại mật khẩu. Mã không hợp lệ hoặc đã hết hạn.",
        };
      }
    },

    update: async (data: unknown): Promise<AuthResult<unknown>> => {
      const params = data as {
        oldPassword?: string;
        currentPassword?: string;
        newPassword?: string;
      };
      const oldPassword = params.oldPassword || params.currentPassword;
      const newPassword = params.newPassword;

      if (!oldPassword || !newPassword) {
        return {
          success: false,
          error: "Vui lòng nhập đầy đủ mật khẩu cũ và mới",
        };
      }

      const token = await sessionService.getAccessToken();
      const authorizationHeader = token
        ? { Authorization: `Bearer ${token}` }
        : undefined;

      try {
        const changePasswordPayload = {
          currentPassword: oldPassword,
          newPassword,
          revokeOtherSessions: true, // Security best practice
          ...(authorizationHeader
            ? {
                fetchOptions: {
                  headers: authorizationHeader,
                },
              }
            : {}),
        };
        const { data: result, error } = await betterAuthClient.changePassword(
          changePasswordPayload,
          authorizationHeader ? { headers: authorizationHeader } : undefined,
        );

        if (error) {
          if (token) {
            return await updatePasswordWithBearerFallback(
              oldPassword,
              newPassword,
            );
          }

          return {
            success: false,
            error: normalizePasswordUpdateError(error),
          };
        }

        return {
          success: true,
          message: "Đổi mật khẩu thành công",
          data: result,
        };
      } catch (error: unknown) {
        if (token) {
          return await updatePasswordWithBearerFallback(
            oldPassword,
            newPassword,
          );
        }

        return {
          success: false,
          error: normalizePasswordUpdateError(error),
        };
      }
    },
    forgotPassword: async (newPassword: string, token: string) => {
      if (!newPassword || !token) {
        return { success: false, error: "Thiếu dữ liệu" };
      }
      return { success: false, error: "Not implemented" };
    },
  },
  registration: {
    completeRegistrationAfterOTP: async (): Promise<AuthResult<void>> => {
      return { success: true };
    },
  },
  getSession: async () => {
    return betterAuthClient.getSession();
  },
  /**
   * AUTH CONTRACT RULE #3
   * All mobile features must resolve identity through one API to prevent
   * token/session drift across screens.
   */
  getAuthenticatedUser: async (): Promise<User | null> => {
    return resolveAuthenticatedUser();
  },
  isAuthenticated: async (): Promise<boolean> => {
    // Mobile SSOT: if bearer token exists in secure preferences, user is authenticated.
    // This avoids false negatives when Better Auth cookie/session is unavailable on native WebView.
    const storedToken = await sessionService.getAccessToken();
    if (storedToken) {
      return true;
    }

    try {
      const session = await resolveWithTimeout(
        betterAuthClient.getSession(),
        SESSION_CHECK_TIMEOUT_MS,
      );
      if (session?.data) return true;
    } catch (error) {
      console.warn("Auth session check failed:", error);
    }

    return false;
  },
  validation: {
    validateRegistration: async (data: { email: string; phone: string }) => {
      const emailExists = await authService.checkEmail(data.email);
      if (emailExists)
        return { valid: false, field: "email", error: "Email exists" };
      const phoneExists = await authService.checkPhone(data.phone);
      if (phoneExists)
        return { valid: false, field: "phone", error: "Phone exists" };
      return { valid: true };
    },
  },
};

export const isAuthenticated = authService.isAuthenticated;
